const withDrawer = (query, options) =>
  options?.openDrawer ? { ...query, openDrawer: true } : query;
const resolveNumberQuery = (record, fallbacks = []) => {
  const keys = ["number", "internal_number", ...fallbacks];

  for (const key of keys) {
    const value = record?.[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return undefined;
};

const SECTION_PATH_MAP = {
  purchase: "purchases",
  sales: "sales",
};

const ROLE_HASH_MAP = {
  buyer: "#customers",
  seller: "#vendors",
};

const ROLE_TO_HASH = (role) => ROLE_HASH_MAP[role] || "#team-members";
const ROUTE_ROLE_ALIAS_MAP = {
  customer: "buyer",
  customers: "buyer",
  buyer: "buyer",
  buyers: "buyer",
  vendor: "seller",
  vendors: "seller",
  seller: "seller",
  sellers: "seller",
};
const resolveUserSearchValue = (record) =>
  record?.gstin || record?.name || record?.shop_name;

const resolveUserRole = (role) => ROUTE_ROLE_ALIAS_MAP[role] || role || "";

const buildUserRoute = (record, role, options = {}) => {
  const normalizedRole = resolveUserRole(role || record?.role);

  return {
    path: "/users",
    ...(ROLE_TO_HASH(normalizedRole)
      ? { hash: ROLE_TO_HASH(normalizedRole) }
      : {}),
    query: withDrawer({ number: resolveUserSearchValue(record) }, options),
  };
};

const buildNoteRoute = (record, options, noteType, metaKey) => {
  const noteFor = record?.[metaKey] || record?.meta_data?.[metaKey];
  const sectionPath = options.section && SECTION_PATH_MAP[options.section];

  let path = `/purchases/${noteType}`;

  if (sectionPath) {
    path = `/${sectionPath}/${noteType}`;
  } else if (noteFor === "invoice") {
    path = `/sales/${noteType}`;
  }

  return {
    path,
    query: withDrawer({ number: record.number }, options),
  };
};

export const TYPE_LABEL_MAP = {
  purchase_order: "Purchase Orders",
  po_number: "Purchase Orders",
  invoice: "Invoices",
  bill: "Purchase Bills",
  credit_note: "Credit Notes",
  debit_note: "Debit Notes",
  proof_of_delivery: "Proof of Deliveries",
  eway_bill: "E-Way Bill",
  customer: "Customers",
  customers: "Customers",
  vendor: "Vendors",
  vendors: "Vendors",
  product: "Products",
  products: "Products",
};

const CATALOGUE_HASH_MAP = (record) =>
  record?.meta_data?.hsn || record?.hsn ? "#products" : "#brands";

const ROUTE_BUILDERS = {
  Invoices: (record, options = {}) => ({
    path: "/sales/invoices",
    query: withDrawer(
      {
        number: resolveNumberQuery(record, [
          "Invoice Number",
          "invoice_number",
        ]),
      },
      options,
    ),
  }),

  Payments: (record, options = {}) => ({
    path: "/accounts/payment-requests",
    query: withDrawer(
      { number: record.internal_number || record.number },
      options,
    ),
  }),

  "Purchase Orders": (record) => {
    const id =
      (typeof record === "string" || typeof record === "number"
        ? record
        : null) ||
      record?._id ||
      record?.id ||
      record?.po_id ||
      record?.purchase_order_id;

    if (!id) {
      return null;
    }

    return {
      path: `/sales/purchase-orders/${id}`,
    };
  },

  "Proof of Deliveries": (record, options = {}) => ({
    path: "/sales/proof-of-deliveries",
    query: withDrawer({ number: record.number }, options),
  }),

  "Purchase Bills": (record, options = {}) => ({
    path: "/purchases/purchase-bills",
    query: withDrawer(
      {
        number: resolveNumberQuery(record, ["Bill Number", "Number"]),
        entityId:
          record?.entity_id ||
          record?._id ||
          record?.id ||
          record?.purchase_bill_id ||
          undefined,
      },
      options,
    ),
  }),

  "E-Way Bill": (record, options = {}) => ({
    path: "/shipping/e-way-bills",
    query: withDrawer(
      { number: record.number || record.internal_number },
      options,
    ),
  }),

  "Credit Notes": (record, options = {}) =>
    buildNoteRoute(record, options, "credit-notes", "cn_for"),

  "Debit Notes": (record, options = {}) =>
    buildNoteRoute(record, options, "debit-notes", "dn_for"),

  Users: (record, options = {}) =>
    buildUserRoute(record, record.meta_data?.role || record?.role, options),

  User: (record, options = {}) =>
    buildUserRoute(record, record?.role || record.meta_data?.role, options),

  Customers: (record, options = {}) => buildUserRoute(record, "buyer", options),

  Vendors: (record, options = {}) => buildUserRoute(record, "seller", options),

  Products: (record, options = {}) => ({
    path: "/catalogue",
    hash: CATALOGUE_HASH_MAP(record),
    query: withDrawer(
      { number: record.name || record.internal_name || record.hsn },
      options,
    ),
  }),
};

export const generateRoute = (label, record, options = {}) => {
  if (!label) {
    return null;
  }

  const cleanLabel = String(label).trim();
  const directBuilder = ROUTE_BUILDERS[cleanLabel];

  if (directBuilder) {
    return directBuilder(record, options);
  }

  const toSnake = (value) =>
    value
      .replace(/[\s-]+/g, "_")
      .replace(/([a-z])([A-Z])/g, "$1_$2")
      .toLowerCase();

  const singularize = (value) => {
    if (value.endsWith("ies")) {
      return value.slice(0, -3) + "y";
    }

    if (value.endsWith("s")) {
      return value.slice(0, -1);
    }

    return value;
  };

  const toTitleCase = (value) =>
    value
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const snake = toSnake(cleanLabel);
  const typeKey = singularize(snake);
  const normalizedTypeKey = ROUTE_ROLE_ALIAS_MAP[typeKey] || typeKey;
  const mappedLabel =
    TYPE_LABEL_MAP[normalizedTypeKey] || TYPE_LABEL_MAP[typeKey];

  if (mappedLabel && ROUTE_BUILDERS[mappedLabel]) {
    return ROUTE_BUILDERS[mappedLabel](record, options);
  }

  const titleCaseLabel = toTitleCase(cleanLabel);

  if (ROUTE_BUILDERS[titleCaseLabel]) {
    return ROUTE_BUILDERS[titleCaseLabel](record, options);
  }

  const titleCaseFromType = mappedLabel ? toTitleCase(mappedLabel) : "";

  if (titleCaseFromType && ROUTE_BUILDERS[titleCaseFromType]) {
    return ROUTE_BUILDERS[titleCaseFromType](record, options);
  }

  return null;
};
